import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';

// Event roster (BK, 10/28/26): one clear list per party or event for the club. Families sorted by last name, each
// child under the parent who signed, consent and signature times, an Arrived tick for the door, a printable list,
// and the signed copy (PDF) for each family. Backend: club-dashboard/guest-event-roster, guest-arrived, guest-signed-copy.

const fmtPhone = p => { const d = String(p || ''); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d; };
const fmtTime = t => t ? new Date(t).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
const fmtDay = d => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';
const age = (birth, on) => {
  const b = new Date(String(birth).slice(0, 10) + 'T12:00:00'); const d = new Date(String(on).slice(0, 10) + 'T12:00:00');
  let a = d.getFullYear() - b.getFullYear();
  if (d.getMonth() < b.getMonth() || (d.getMonth() === b.getMonth() && d.getDate() < b.getDate())) a -= 1;
  return a;
};
const esc = t => String(t ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

export default function GuestEventRoster({ clubId, code, link, onClose }) {
  const { get, post, getBlob } = useApi();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(() => get(`/club-dashboard/guest-event-roster?club=${clubId}&code=${encodeURIComponent(code)}`)
    .then(setData).catch(e => setErr(e.message)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clubId, code]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const tick = async (kind, id, arrived) => {
    try { await post('/club-dashboard/guest-arrived', { club: clubId, kind, id, arrived }); load(); }
    catch (e) { alert(e.message); }
  };
  const openCopy = async id => {
    try { const b = await getBlob(`/club-dashboard/guest-signed-copy?id=${id}`); window.open(URL.createObjectURL(b), '_blank'); }
    catch (e) { alert(e.message); }
  };

  const ev = data?.event; const tot = data?.totals; const fams = data?.families || [];
  const term = q.trim().toLowerCase();
  const shown = !term ? fams : fams.filter(f => [f.full_name, f.email, f.phone10, ...f.children.map(k => `${k.firstName} ${k.lastName}`)]
    .some(v => String(v || '').toLowerCase().includes(term)));

  const print = () => {
    const rows = fams.map(f => {
      const kids = f.children.map(k => `<tr><td></td><td>${esc(k.firstName)} ${esc(k.lastName)} (age ${age(k.birthDate, ev.event_date)})</td><td>Parent consent signed ${esc(fmtTime(k.consentSignedAt))}</td><td class="box"></td></tr>`).join('');
      return `<tr class="fam"><td class="box"></td><td><b>${esc(f.full_name)}</b>${f.checkin_role === 'children' ? ' (not playing)' : ''}${f.adultUnconfirmed ? ' <i>(check: signed as adult)</i>' : ''}${f.sharedPhone ? ' <i>(check: same phone as another family)</i>' : ''}<br><span>${esc(fmtPhone(f.phone10))} · ${esc(f.home_city || '')} ${esc(f.home_zip || '')}</span></td><td>Waiver signed ${esc(fmtTime(f.waiver_accepted_at))}</td><td class="box"></td></tr>${kids}`;
    }).join('');
    const w = window.open('', '_blank');
    w.document.write(`<!doctype html><html><head><title>${esc(ev.name)} roster</title><style>
      body{font-family:Arial,sans-serif;margin:28px;color:#111} h1{font-size:20px;margin:0} .sub{color:#555;margin:4px 0 14px;font-size:13px}
      table{width:100%;border-collapse:collapse;font-size:12px} th{text-align:left;border-bottom:2px solid #111;padding:6px 4px}
      td{padding:6px 4px;border-bottom:1px solid #ddd;vertical-align:top} tr.fam td{border-top:1px solid #999} span{color:#555}
      td.box{width:22px} td.box:before{content:'';display:inline-block;width:13px;height:13px;border:1.5px solid #333}
      tr td:first-child.box:before{} @media print{body{margin:12px}}</style></head><body>
      <h1>${esc(ev.name)}</h1><div class="sub">${esc(fmtDay(ev.event_date))} · ${tot.families} families · ${tot.adultsPlaying} adults · ${tot.children} children · every person listed has a signed waiver on file</div>
      <table><thead><tr><th></th><th>Name</th><th>Signed</th><th>Arrived</th></tr></thead><tbody>${rows || '<tr><td colspan="4">No one has signed yet.</td></tr>'}</tbody></table>
      <script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, width: '100%', maxHeight: '92vh', overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
        {err && <div style={{ color: '#ef4444' }}>{err}</div>}
        {!data && !err && <div style={{ color: 'var(--text-muted)' }}>Loading…</div>}
        {ev && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>🎉 {ev.name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{fmtDay(ev.event_date)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => navigator.clipboard?.writeText(link)}>Copy sign-up link</button>
                <button className="btn btn-secondary btn-sm" onClick={print} disabled={!fams.length}>Print roster</button>
                <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, margin: '16px 0' }}>
              {[['Families', tot.families], ['Adults playing', tot.adultsPlaying], ['Children', tot.children], ['Arrived', `${tot.arrived} of ${tot.people}`]].map(([l, v]) => (
                <div key={l} className="card" style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{l}</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{v}</div>
                </div>
              ))}
            </div>

            {tot.warnings > 0 && (
              <div style={{ fontSize: 12.5, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', color: '#f59e0b', borderRadius: 8, padding: '8px 12px', marginBottom: 10, lineHeight: 1.5 }}>
                {tot.warnings} sign-up{tot.warnings === 1 ? ' needs' : 's need'} a look at the desk (marked below). If a child signed in as an adult, ask their parent to sign for them through the same link, choosing "Only my child(ren)".
              </div>
            )}
            <input className="field-input" placeholder="Find a family or child" value={q} onChange={e => setQ(e.target.value)} style={{ marginBottom: 8 }} />
            {!fams.length && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>No one has signed yet. Send the sign-up link to the host to share with every family.</div>}

            {shown.map(f => (
              <div key={f.id} style={{ borderTop: '1px solid var(--border)', padding: '12px 0' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: f.checkin_role === 'children' ? 'default' : 'pointer' }}>
                    {f.checkin_role !== 'children'
                      ? <input type="checkbox" checked={!!f.arrived_at} onChange={e => tick('adult', f.id, e.target.checked)} style={{ width: 18, height: 18, marginTop: 2 }} />
                      : <span style={{ width: 18 }} />}
                    <span>
                      <span style={{ fontSize: 15, fontWeight: 700 }}>{f.full_name}</span>
                      {f.checkin_role === 'children' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}> · parent, not playing</span>}
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        {fmtPhone(f.phone10)} · {f.email}
                        {f.home_zip && <> · {f.home_city} {f.home_zip}</>}
                        {f.in_market === false && <span className="badge" style={{ marginLeft: 6, background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>Out of area</span>}
                        {f.sharedPhone && <span className="badge" style={{ marginLeft: 6, background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }} title="Another family at this event signed with the same phone. Check it is really each parent's own number.">Same phone as another family</span>}
                        {f.adultUnconfirmed && <span className="badge" style={{ marginLeft: 6, background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }} title="Signed in as an adult without confirming 18 or older. If this is a child, a parent needs to sign for them.">Signed as adult, not confirmed 18+</span>}
                      </div>
                      <div style={{ fontSize: 12, marginTop: 2 }}>
                        Waiver signed {fmtTime(f.waiver_accepted_at)}
                        {f.confirm_text_status?.startsWith('sent') && <span style={{ color: 'var(--text-muted)' }}> · confirmation texted</span>}
                        {(f.interests || []).includes('junior_programs') && <span className="badge" style={{ marginLeft: 6, background: 'rgba(168,85,247,0.13)', color: '#a855f7' }}>Wants junior program info</span>}
                      </div>
                    </span>
                  </label>
                  <button className="btn btn-secondary btn-sm" onClick={() => openCopy(f.id)}>Signed copy</button>
                </div>
                {f.children.map(k => (
                  <label key={k.id} style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '8px 0 0 28px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!k.arrivedAt} onChange={e => tick('child', k.id, e.target.checked)} style={{ width: 18, height: 18 }} />
                    <span style={{ fontSize: 14 }}><strong>{k.firstName} {k.lastName}</strong>
                      <span style={{ color: 'var(--text-muted)' }}> · age {age(k.birthDate, ev.event_date)}</span></span>
                    <span className="badge" style={{ background: 'rgba(34,197,94,0.13)', color: '#22c55e' }}>Parent consent on file</span>
                  </label>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
